import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import { IconButton, Typography, FormControl, Select, MenuItem, Checkbox, FormControlLabel } from '@material-ui/core';
import { Close, AddBox } from '@material-ui/icons';

import AddOrUpdateField, { FieldData } from './AddOrUpdateField';
import DynamicForm from '../../components/DynamicForm';
import { Schema as SchemaType, uiSchema as uiSchemaType } from '../../components/DynamicForm/types';
import { FIELD_TYPES } from '../../components/DynamicForm/consts';
import FormTextField from '../../components/DynamicForm/FormTextField';
import { dialogSizes } from '../../constants/dialogSizesConstant';

import './CreateOrUpdateForm.scss';

export enum STATE {
    _UPDATE,
    _CREATE,
};

type StateType = typeof STATE[keyof typeof STATE];

export const FORM_TYPES = ['Standard', 'Meet'] as const;
export type FormType = typeof FORM_TYPES[number];

interface CreateOrUpdateFormProps {
    open: boolean;
    state: StateType;
    formTemplate?: any;
    defaultFormType?: FormType;
    defaultFormOptional?: boolean;
    hideFormType?: boolean;
    onClose?: () => void;
    onSubmit: (_payload: { name: string, schema: SchemaType, uiSchema: uiSchemaType, type: FormType, optional: boolean }) => void;
}

const DEFAULT_UI_SCHEMA_PROPS = {
    fieldsPerRow: 0,
    label: false,
    placeholder: true,
    isRequired: true,
    isDraggable: true,
    isRemovable: true,
    isEditable: true,
};

const CreateOrUpdateForm = ({
    open,
    state,
    formTemplate,
    defaultFormType,
    defaultFormOptional = false,
    hideFormType = false,
    onClose,
    onSubmit,
}: CreateOrUpdateFormProps) => {
    const { t } = useTranslation();
    const [formName, setFormName] = useState<string>('');
    const [formType, setFormType] = useState<FormType>(defaultFormType || 'Standard');
    const [isOptional, setIsOptional] = useState<boolean>(defaultFormOptional || false);

    const defaultFormSchema:SchemaType = {
        properties: {
            firstName: {
                type: 'object',
                title: t('COMMON_FIRST_NAME'),
            },
            email: {
                type: 'object',
                title: 'Email',
                format: t('COMMON_EMAIL'),
            },
            lastName: {
                type: 'object',
                title: t('COMMON_LAST_NAME'),
            }
        }
    };

    const defaultUIFormSchema:uiSchemaType = {
        properties: {
            email: { ...DEFAULT_UI_SCHEMA_PROPS, type: FIELD_TYPES.Input, order: 3, isRemovable: false, isEditable: false },
            firstName: { ...DEFAULT_UI_SCHEMA_PROPS, type: FIELD_TYPES.Input, order: 1, isRemovable: false, isEditable: false },
            lastName: { ...DEFAULT_UI_SCHEMA_PROPS, type: FIELD_TYPES.Input, order: 2, isRemovable: false, isEditable: false },
        }
    };

    const [formSchema, setFormSchema] = useState<SchemaType>(defaultFormSchema);
    const [uiFormSchema, setUiFormSchema] = useState<uiSchemaType>(defaultUIFormSchema);

    const [isAddingNewField, setIsAddingNewField] = useState<boolean>(false);
    const [isUpdatingField, setIsUpdatingField] = useState<boolean>(false);
    const [updatingFieldId, setUpdatingFieldId] = useState<string>('');

    const resetForm = () => {
        setFormName('');
        setFormType(defaultFormType || 'Standard');
        setFormSchema(defaultFormSchema);
        setUiFormSchema(defaultUIFormSchema);
        setIsOptional(defaultFormOptional || false);
    };

    const handleCreateNewForm = () => {
        const payload = {
            name: formName,
            schema: formSchema,
            uiSchema: uiFormSchema,
            type: formType,
            optional: isOptional,
        }
        onSubmit(payload);
        resetForm();
    };

    const handleAddOrUpdateField = (field: any) => {
        setFormSchema((prev: SchemaType) => {
            return {
                ...prev,
                properties: {
                    ...prev.properties,
                    [updatingFieldId || field.key]: {
                        type: 'object',
                        title: field.name,
                    }
                }
            }
        });
        setUiFormSchema((prev: uiSchemaType) => {
            return {
                ...prev,
                properties: {
                    ...prev.properties,
                    [updatingFieldId || field.key]: {
                        ...DEFAULT_UI_SCHEMA_PROPS,
                        isEditable: field.isEditable ?? DEFAULT_UI_SCHEMA_PROPS.isEditable,
                        type: field.type,
                        options: field.options,
                        label: field.label || false,
                        isRequired: field.isRequired,
                        order: field.order ?? Object.keys(prev.properties).length + 1,
                    }
                }
            }
        });
    };

    const handleRemoveField = (key: string) => {
        handleResetUpdate();
        setFormSchema((prev: SchemaType) => {
            const { [key]: _, ...rest } = prev.properties;
            return {
                ...prev,
                properties: rest,
            }
        });
        setUiFormSchema((prev: uiSchemaType) => {
            const { [key]: _, ...rest } = prev.properties;
            return {
                ...prev,
                properties: rest,
            }
        });
    };

    // adds the order propery to the respective item in the passed object
    const applyOrderPropery = (orderedArr: Array<{id: string}>, obj: any): any => {
        const newObj: Record<string, any> = {};
        orderedArr.forEach((el: any, index: number)=> {
            const key = el.id;
            newObj[key] = { ...obj[key], order: index + 1};
        });
        return newObj;
    };

    const handleFieldClick = (fieldId: string) => {
        setIsUpdatingField(true);
        setUpdatingFieldId(fieldId);
    };

    const handleResetUpdate = () => {
        setIsUpdatingField(false);
        setUpdatingFieldId('');
    };

    const handleReorderField = (fields: any) => {
        setUiFormSchema((prev: uiSchemaType) => {
            return {
                ...prev,
                properties: applyOrderPropery(fields, prev.properties),
            }
        });
    };

    const cancelAddOrUpdate = () => {
        setIsAddingNewField(false);
        setIsUpdatingField(false);
    };

    const addNewField = () => {
        setIsAddingNewField(true);
        handleResetUpdate();
    };

    const getFieldData = () => {
        if (updatingFieldId) {
            const schemaData = formSchema.properties[updatingFieldId];
            const uiSchemaData = uiFormSchema.properties[updatingFieldId];
            if (schemaData && uiSchemaData) {
                return {
                    title: schemaData.title,
                    type: uiSchemaData.type,
                    isRequired: uiSchemaData.isRequired,
                    options: uiSchemaData.options
                } as FieldData;
            } else {
                return undefined;
            }
        }
        return undefined;
    };

    useEffect(() => {
        if (formTemplate) {
            setFormName(formTemplate.name);
            setFormType(formTemplate.type || 'Standard');
            setIsOptional(formTemplate.optional || false);
            setFormSchema(formTemplate.schema);
            setUiFormSchema(formTemplate.styling);
        }
    }, [formTemplate]);

    const titleText = state === STATE._CREATE ? t('MODAL_CREATE_FORM_TITLE') : t('MODAL_UPDATE_FORM_TITLE');
    const btnText = state === STATE._CREATE ? t('COMMON_CREATE') : t('COMMON_UPDATE');

    return (
        <>
            <Dialog
                id="createOrUpdateForm"
                fullWidth={true}
                maxWidth={dialogSizes.SMALL as any}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                open={open}
            >
                <DialogTitle className="formDialogTitle" disableTypography>
                    <Typography variant="h6">{titleText}</Typography>
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                {!isAddingNewField && !isUpdatingField &&
                    <>
                        <div className="formName">
                            <FormTextField
                                id="form-name"
                                title={t('CREATE_OR_UPDATE_FORM_NAME_YOUR_FORM')}
                                label={true}
                                isRequired={true}
                                value={formName}
                                autoComplete="form-name"
                                onChange={(_, val) => setFormName(val)}
                            />
                        </div>
                        {!hideFormType && <div className="formType">
                            <div className="formTypeLabel">{t('COMMON_TYPE')}</div>
                            <FormControl variant="outlined" size="small" fullWidth disabled={state === STATE._UPDATE}>
                                <Select
                                    value={formType}
                                    onChange={(e) => setFormType(e.target.value as FormType)}
                                >
                                    {FORM_TYPES.map(type => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isOptional}
                                        onChange={(e) => setIsOptional(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={t('COMMON_OPTIONAL')}
                            />
                        </div>}
                        {(formSchema && Object.keys(formSchema.properties).length) ?
                            <div className="formContainer">
                                <div className="formTItle">{t('COMMON_FORM_FIELDS')}</div>
                                <DynamicForm
                                    className="formResult"
                                    isEditMode={true}
                                    schema={formSchema}
                                    uiSchema={uiFormSchema}
                                    onFieldClick={handleFieldClick}
                                    onReorderField={handleReorderField}
                                    onRemoveField={handleRemoveField}
                                    onDefaultFieldChange={(key, value) => {
                                        if (key !== "firstName" && key !== "lastName" && key !== "email") {
                                            return;
                                        }
                                        handleAddOrUpdateField({ key,
                                            name: value,
                                            type: uiFormSchema.properties[key].type,
                                            isRequired: uiFormSchema.properties[key].isRequired,
                                            order: uiFormSchema.properties[key].order,
                                            isEditable: false,
                                        });
                                    }}
                                />
                            </div>
                            : null
                        }
                    </>
                }
                {(isAddingNewField || isUpdatingField) &&
                    <AddOrUpdateField
                        data={getFieldData()}
                        onChange={handleAddOrUpdateField}
                        onCancel={cancelAddOrUpdate}
                    />
                }
                <div className="formActions">
                    {(!isAddingNewField && !isUpdatingField) &&
                        <>
                            <div className="formTopActions">
                                <IconButton
                                    style={{ padding: "4px", borderRadius: "4px" }}
                                    disableRipple
                                    onClick={addNewField}
                                >
                                    <AddBox style={{ fontSize: '46px' }} color="primary" />
                                    <Typography style={{ fontSize: '12px', marginLeft: '8px' }}>
                                        {t('COMMON_NEW_FIELD')}
                                    </Typography>
                                </IconButton>
                            </div>
                            <div className="formBottomActions">
                                <Button
                                    variant="contained"
                                    className={`defaultActionBtn`}
                                    onClick={handleCreateNewForm}
                                    disabled={!formName}
                                >
                                    {btnText}
                                </Button>
                            </div>
                        </>
                    }
                </div>
            </Dialog>
        </>
    );
};

export default CreateOrUpdateForm;
